import React, { useEffect, useMemo, useState } from "react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { useLocation, useNavigate, useRevalidator } from "react-router-dom";
import { Input } from "../../../components/inputs/Input";
import CustomSelect from "../../../components/customSelect";
import CheckboxDropdown from "../../../components/CheckboxDropdown";
import {
  organizationStore,
  useOrganizationStore,
} from "../../../store/organization";
import {
  renderLocationOptions,
  renderUsernameOptions,
  renderVendorOptions,
} from "../../reports/utils";
import { Vendor } from "../../../types/vendor";
import { VENDORS_LIST_QUERY } from "../../vendors/queries";
import { createApolloClient } from "../../../providers/ApolloClientFactory";
import { PrimaryButton, SecondaryButton } from "../../../components/Button";
import { userStore } from "../../../store/user";
import { eventStore } from "../../../store/event";
import { GET_LOCATIONS_REPORT_FILTER } from "../../locations/queries";
import { PAYMENT_TYPE_OPTIONS } from "../paymentTypes";

const { getState } = userStore;
const { getState: getOrganizationState } = organizationStore;
const { getState: getEventState } = eventStore;

type NullableString = string | "";

interface OrderFilterProps {
  paymentProcessorOptions?: { value: string; label: string }[];
  selectedPaymentProcessors?: string[] | null;
  onPaymentProcessorChange?: (value: string[] | null) => void;
  onExportCSV?: () => void;
}

// Vendor/location option values arrive from GraphQL as numbers while URL params
// are always strings, so CustomSelect's `options.find(o => o.value === value)`
// never matched and a selected vendor vanished from the dropdown after a
// search. Normalizing every option value to a string keeps form state, URL
// params, and option values in one type.
const toStringOptions = (options: { value: any; label: string }[]) =>
  options.map((option) => ({ ...option, value: String(option.value) }));

// Filters are grouped so the form reads as four short questions — who rang it
// up, how it was paid, who the customer was, and which ID you have in hand —
// instead of one undifferentiated grid of fourteen boxes.
//
// Sections collapse but start open: the whole form is visible on arrival, and
// collapsing is a way to get a long-running search out of the way rather than
// something you have to undo before you can filter. A collapsed section still
// shows how many of its filters are set, so nothing narrows the results from
// behind a closed panel without saying so.
const FilterSection = ({
  title,
  activeCount,
  children,
}: {
  title: string;
  activeCount: number;
  children: React.ReactNode;
}) => {
  const [open, setOpen] = useState(true);

  return (
    <section className="pt-4 first:pt-1">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary-600 hover:text-primary-700 focus:outline-none"
      >
        <ChevronDownIcon
          className={`h-4 w-4 shrink-0 transition-transform ${open ? "" : "-rotate-90"}`}
          aria-hidden="true"
        />
        <span className="whitespace-nowrap">{title}</span>
        {!open && activeCount > 0 && (
          <span className="rounded-full bg-gray-200 px-2 py-0.5 text-[11px] font-semibold normal-case tracking-normal text-gray-700">
            {activeCount}
          </span>
        )}
        <span className="ml-1 h-px flex-1 bg-gray-200" />
      </button>
      {open && (
        <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {children}
        </div>
      )}
    </section>
  );
};

function OrderFilter({ paymentProcessorOptions, selectedPaymentProcessors, onPaymentProcessorChange, onExportCSV }: OrderFilterProps) {
  const revalidator = useRevalidator();
  const navigate = useNavigate();
  const client = createApolloClient();
  const organizationId = getOrganizationState().organizationId;
  const { eventId } = getEventState();
  const user = getState().user;

  const [locations, setLocations] = useState<Location[]>([]);
  const searchLocation = useLocation();
  const searchParams = new URLSearchParams(searchLocation.search);

  const vendorId = (searchParams.get("vendor") ?? "") as NullableString;
  const excludedVendorIds = searchParams.getAll("excludeVendor");
  const locationId = (searchParams.get("location") ?? "") as NullableString;
  const username = (searchParams.get("username") ?? "") as NullableString;
  const last4ofCC = (searchParams.get("last4ofCC") ?? "") as NullableString;
  const cardTypeFirst = (searchParams.get("cardType") ?? "") as NullableString; // keep the first one for UI
  const paymentTypes = searchParams.getAll("paymentType");
  // The URL carries the amount in cents (see executeSearch). Show it as dollars,
  // otherwise a reload puts "1250" in the field and the next search reads it as
  // $1,250.00.
  const transactionAmountCents = Number.parseInt(searchParams.get("transactionAmount") ?? "", 10);
  const transactionAmount = (
    Number.isFinite(transactionAmountCents) ? (transactionAmountCents / 100).toFixed(2) : ""
  ) as NullableString;
  const paymentRefId = (searchParams.get("paymentRefId") ?? "") as NullableString;
  const mxRefId = (searchParams.get("mxRefId") ?? "") as NullableString;
  const cashlessAssetId = (searchParams.get("cashlessAssetId") ?? "") as NullableString;
  const email = (searchParams.get("email") ?? "") as NullableString;
  const phoneNumber = (searchParams.get("phoneNumber") ?? "") as NullableString;
  const orderId = (searchParams.get("orderId") ?? "") as NullableString;
  const attendeeId = (searchParams.get("attendeeId") ?? "") as NullableString;

  const users = useOrganizationStore((state) => state.clerks);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [resetKey, setResetKey] = useState(0);

  const vendorOptions = useMemo(
    () => toStringOptions(renderVendorOptions(vendors ?? [])),
    [vendors]
  );
  const locationOptions = useMemo(
    () => toStringOptions(renderLocationOptions(locations ?? [])),
    [locations]
  );

  const cardTypeOptions = [
    { value: "American Express", label: "American Express" },
    { value: "Diner's Club International", label: "Diner's Club International" },
    { value: "Discover", label: "Discover" },
    { value: "Interac", label: "Interac" },
    { value: "Maestro", label: "Maestro" },
    { value: "MasterCard", label: "MasterCard" },
    { value: "UnionPay", label: "UnionPay" },
    { value: "Visa", label: "Visa" },
  ];

  // Rich synonyms set; first element should be the canonical label
  const cardTypeSynonyms: Record<string, string[]> = {
    "American Express": ["American Express", "Amex", "AMEX", "amex", "american express"],
    "Diner's Club International": [
      "Diner's Club International",
      "Diners Club",
      "diners club",
      "diners",
    ],
    Discover: ["Discover", "DISC", "disc", "discover"],
    Interac: ["Interac", "interac"],
    Maestro: ["Maestro", "maestro"],
    MasterCard: ["MasterCard", "Mastercard", "MC", "mc", "master card"],
    UnionPay: ["UnionPay", "Union Pay", "CUP", "cup"],
    Visa: ["Visa", "VISA", "visa"],
  };

  const emptyFields = {
    vendor: "",
    excludeVendor: [] as string[],
    location: "",
    username: "",
    last4ofCC: "",
    cardType: "",
    paymentType: [] as string[],
    transactionAmount: "",
    paymentRefId: "",
    mxRefId: "",
    cashlessAssetId: "",
    phoneNumber: "",
    email: "",
    orderId: "",
    attendeeId: "",
  };

  const [formFields, setFormFields] = useState({
    ...emptyFields,
    vendor: vendorId,
    excludeVendor: excludedVendorIds,
    location: locationId,
    username: username,
    last4ofCC: last4ofCC,
    cardType: cardTypeFirst, // keep canonical label for the UI
    paymentType: paymentTypes,
    transactionAmount: transactionAmount,
    paymentRefId: paymentRefId,
    mxRefId: mxRefId,
    cashlessAssetId: cashlessAssetId,
    phoneNumber: phoneNumber,
    email: email,
    orderId: orderId,
    attendeeId: attendeeId,
  });

  // Normalize values from either strings, DOM events, or { value, label } objects
  const extractValue = (input: any) => {
    if (input && typeof input === "object" && !Array.isArray(input)) {
      if ("value" in input) return input.value;
      if ("target" in input && input.target && "value" in input.target) return input.target.value;
    }
    return input ?? "";
  };

  // Only lowercase for fields that truly benefit
  const LOWERCASE_FIELDS = new Set(["username", "email"]);

  const handleFieldChange = (raw: any, fieldName: string): void => {
    let value = extractValue(raw);

    if (typeof value === "string") {
      if (fieldName === "phoneNumber") {
        value = value.replace(/-/g, "");
      } else if (LOWERCASE_FIELDS.has(fieldName)) {
        value = value.toLowerCase();
      }
      // Do NOT lowercase select values like cardType
    }

    setFormFields((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  // Multi-selects hand back an array (or null when cleared).
  const handleMultiChange = (raw: string[] | null, fieldName: string): void => {
    setFormFields((prev) => ({
      ...prev,
      [fieldName]: raw ?? [],
    }));
  };

  const isSet = (value: unknown) =>
    Array.isArray(value) ? value.length > 0 : String(value ?? "").trim() !== "";

  // Which fields live in which section, so a collapsed header can report its
  // own count. The Payment Processor dropdown is filtered client-side and lives
  // in OrdersList state rather than formFields, so it is counted separately —
  // its "everything selected" state means no filter, same as an empty field.
  const SECTION_FIELDS = {
    scope: ["vendor", "excludeVendor", "location", "username"],
    payment: ["paymentType", "cardType", "last4ofCC", "transactionAmount"],
    customer: ["email", "phoneNumber", "attendeeId"],
    identifiers: ["orderId", "paymentRefId", "mxRefId", "cashlessAssetId"],
  } as const;

  const processorFilterActive =
    !!selectedPaymentProcessors &&
    selectedPaymentProcessors.length > 0 &&
    selectedPaymentProcessors.length < (paymentProcessorOptions?.length ?? 0);

  const sectionCounts = useMemo(() => {
    const count = (keys: readonly string[]) =>
      keys.filter((key) => isSet((formFields as Record<string, unknown>)[key])).length;

    return {
      scope: count(SECTION_FIELDS.scope),
      payment: count(SECTION_FIELDS.payment) + (processorFilterActive ? 1 : 0),
      customer: count(SECTION_FIELDS.customer),
      identifiers: count(SECTION_FIELDS.identifiers),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formFields, processorFilterActive]);

  const activeFilterCount =
    sectionCounts.scope +
    sectionCounts.payment +
    sectionCounts.customer +
    sectionCounts.identifiers;

  const executeSearch = () => {
    if (formFields.last4ofCC && formFields.last4ofCC.length !== 4) {
      alert('The "Last 4 Digits of Credit Card" field must contain exactly 4 digits.');
      return;
    }

    // fresh params to avoid leftover duplicates
    const nextParams = new URLSearchParams();

    // Helper to set or skip empty fields
    const setParamIfValue = (key: string, val?: string | number | null) => {
      const v = (val ?? "").toString().trim();
      if (v !== "") nextParams.set(key, v);
    };

    // Normalize amount to cents (string)
    let normalizedAmount = formFields.transactionAmount;
    if (normalizedAmount !== "") {
      const floatAmount = parseFloat(normalizedAmount);
      normalizedAmount = Number.isFinite(floatAmount)
          ? Math.round(floatAmount * 100).toString()
          : "";
    }

    // Write all simple fields (skip empties)
    setParamIfValue("vendor", formFields.vendor);
    setParamIfValue("location", formFields.location);
    setParamIfValue("username", formFields.username);
    setParamIfValue("last4ofCC", formFields.last4ofCC);
    setParamIfValue("transactionAmount", normalizedAmount);
    setParamIfValue("paymentRefId", formFields.paymentRefId);
    setParamIfValue("mxRefId", formFields.mxRefId);
    setParamIfValue("cashlessAssetId", formFields.cashlessAssetId);
    setParamIfValue("email", formFields.email);
    setParamIfValue("phoneNumber", formFields.phoneNumber);
    setParamIfValue("orderId", formFields.orderId);
    setParamIfValue("attendeeId", formFields.attendeeId);

    // Multi-value fields repeat their key; the loader reads them with getAll().
    formFields.excludeVendor.forEach((id) => nextParams.append("excludeVendor", id));
    formFields.paymentType.forEach((type) => nextParams.append("paymentType", type));

    // CardType: append multiple values under the SAME key.
    // First value is the selected label (so UI rehydrates),
    // followed by alternates. Loader can consume all with getAll('cardType').
    if (formFields.cardType) {
      const primary = formFields.cardType;
      const alts = cardTypeSynonyms[primary] ?? [primary];
      // Ensure selected label is first, dedupe the rest.
      const all = Array.from(new Set([primary, ...alts]));
      // First append preserves get('cardType') === label for UI
      all.forEach((v) => nextParams.append("cardType", v));
    }

    navigate({
      pathname: "/orders/list",
      search: nextParams.toString(),
    });

    revalidator.revalidate();
  };

  const clearFilters = () => {
    setFormFields({ ...emptyFields });
    onPaymentProcessorChange?.(null);

    // Blow away all params
    navigate({
      pathname: "/orders/list",
      search: "",
    });

    // Force uncontrolled selects/inputs to reset
    setResetKey((prevKey) => prevKey + 1);
    revalidator.revalidate();
  };

  const fetchVendor = async () => {
    let where: any = organizationId
        ? {
          organization_id: { _eq: organizationId },
        }
        : {};

    let locationWhere: any = organizationId
        ? {
          event: { organization_id: { _eq: organizationId } },
        }
        : {};

    if (eventId) {
      locationWhere = { ...locationWhere, event_id: { _eq: eventId } };
    }
    if (user?.vendors) {
      where = { ...where, id: { _in: user?.vendors } };
      locationWhere = { vendor_id: { _in: user?.vendors } };
    }
    if (user?.events) {
      locationWhere = { event_id: { _in: user?.events } };
    }

    const [{ data: vendorData }, { data: locationData }] = await Promise.all([
      client.query({
        query: VENDORS_LIST_QUERY,
        variables: { where },
      }),
      client.query({
        query: GET_LOCATIONS_REPORT_FILTER,
        variables: { locationWhere },
      }),
    ]);

    setVendors(vendorData?.vendors || []);
    setLocations(locationData?.locations || []);
  };

  useEffect(() => {
    fetchVendor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  return (
      <div className="px-4">
        <FilterSection title="Scope" activeCount={sectionCounts.scope}>
          {!user?.vendor_id && (
              <CustomSelect
                  name="vendor"
                  title="Vendor"
                  key={`vendor-${resetKey}`}
                  value={formFields.vendor}
                  onChange={(e) => handleFieldChange(e, "vendor")}
                  isClearable
                  options={vendorOptions}
              />
          )}

          {!user?.vendor_id && (
              <CustomSelect
                  name="excludeVendor"
                  title="Exclude Vendor"
                  key={`excludeVendor-${resetKey}`}
                  value={formFields.excludeVendor}
                  onChange={(value) => handleMultiChange(value, "excludeVendor")}
                  multi
                  closeOnSelect={false}
                  options={vendorOptions}
              />
          )}

          <CustomSelect
              name="location"
              title="Location"
              key={`location-${resetKey}`}
              value={formFields.location}
              onChange={(e) => handleFieldChange(e, "location")}
              isClearable
              options={locationOptions}
          />

          {!user?.vendor_id && (
              <CustomSelect
                  name="username"
                  title="Username"
                  key={`username-${resetKey}`}
                  value={formFields.username}
                  onChange={(e) => handleFieldChange(e, "username")}
                  isClearable
                  options={renderUsernameOptions(users ?? [])}
              />
          )}
        </FilterSection>

        <FilterSection title="Payment" activeCount={sectionCounts.payment}>
          <CustomSelect
              name="paymentType"
              title="Tender"
              key={`paymentType-${resetKey}`}
              value={formFields.paymentType}
              onChange={(value) => handleMultiChange(value, "paymentType")}
              multi
              closeOnSelect={false}
              options={PAYMENT_TYPE_OPTIONS}
          />

          <CustomSelect
              name="cardType"
              title="Card Type"
              key={`cardType-${resetKey}`}
              value={formFields.cardType}
              onChange={(e) => handleFieldChange(e, "cardType")}
              isClearable
              options={cardTypeOptions}
          />

          {paymentProcessorOptions && paymentProcessorOptions.length > 0 && (
            <div className="relative">
              <CheckboxDropdown
                title="Payment Processor"
                options={paymentProcessorOptions}
                selected={selectedPaymentProcessors ?? null}
                onChange={(value) => onPaymentProcessorChange?.(value)}
              />
            </div>
          )}

          <Input
              label="Last 4 Digits of Credit Card"
              name="last4ofCC"
              value={formFields.last4ofCC}
              placeholder="1234"
              onChange={(e) => handleFieldChange(e ?? "", "last4ofCC")}
              onEnter={executeSearch}
          />

          <Input
              label="Transaction Amount"
              name="transactionAmount"
              placeholder="$0.00"
              value={formFields.transactionAmount}
              onChange={(e) => handleFieldChange(e ?? "", "transactionAmount")}
              onEnter={executeSearch}
          />
        </FilterSection>

        <FilterSection title="Customer" activeCount={sectionCounts.customer}>
          <Input
              label="Customer Email"
              name="email"
              placeholder="Customer Email"
              value={formFields.email}
              onChange={(e) => handleFieldChange(e ?? "", "email")}
              onEnter={executeSearch}
          />

          <Input
              label="Phone Number"
              name="phoneNumber"
              placeholder="Phone Number"
              value={formFields.phoneNumber}
              onChange={(e) => handleFieldChange(e ?? "", "phoneNumber")}
              onEnter={executeSearch}
          />

          <Input
              label="Attendee ID"
              name="attendeeId"
              placeholder="Attendee ID"
              value={formFields.attendeeId}
              onChange={(e) => handleFieldChange(e ?? "", "attendeeId")}
              onEnter={executeSearch}
          />
        </FilterSection>

        <FilterSection title="Identifiers" activeCount={sectionCounts.identifiers}>
          <Input
              label="Ronin Order ID"
              name="orderId"
              placeholder="Ronin Order ID"
              value={formFields.orderId}
              onChange={(e) => handleFieldChange(e ?? "", "orderId")}
              onEnter={executeSearch}
          />

          <Input
              label="Ronin Reference ID"
              name="paymentRefId"
              placeholder="Ronin Reference ID"
              value={formFields.paymentRefId}
              onChange={(e) => handleFieldChange(e ?? "", "paymentRefId")}
              onEnter={executeSearch}
          />

          <Input
              label="Processor Reference ID"
              name="mxRefId"
              placeholder="Processor Reference ID"
              value={formFields.mxRefId}
              onChange={(e) => handleFieldChange(e ?? "", "mxRefId")}
              onEnter={executeSearch}
          />

          <Input
              label="Cashless Asset ID"
              name="cashlessAssetId"
              placeholder="Cashless Asset ID"
              value={formFields.cashlessAssetId}
              onChange={(e) => handleFieldChange(e ?? "", "cashlessAssetId")}
              onEnter={executeSearch}
          />
        </FilterSection>

        <div className="mt-4 flex flex-col gap-2 border-t border-gray-200 pt-3 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-sm text-gray-500">
            {activeFilterCount === 0
              ? "No filters set"
              : `${activeFilterCount} filter${activeFilterCount === 1 ? "" : "s"} set`}
          </span>
          <div className="text-right">
            <PrimaryButton onClick={clearFilters}>Clear Filters</PrimaryButton>
            <PrimaryButton onClick={executeSearch}>Search</PrimaryButton>
            {onExportCSV && <SecondaryButton onClick={onExportCSV}>Export to CSV</SecondaryButton>}
          </div>
        </div>
      </div>
  );
}

export default OrderFilter;
